import { useEffect, useRef, useState } from "react";
import { WINDOW } from "../../content/copy";
import { Reveal } from "../Reveal";
import { SectionMark, RegMarks } from "../fx/Marginalia";

const TOTAL = 30;
const SEGMENTS = Array.from({ length: TOTAL }, (_, i) => i);
const stamp = (elapsed) => `03:47:${String(12 + elapsed).padStart(2, "0")}`;

const Readout = ({ left, rejected }) => (
  <div className="c-readout">
    <span className="c-label mono" aria-hidden="true">{rejected ? "TIMER ARRESTATO" : "T-MINUS · RATIFICA AUTOMATICA"}</span>
    <div className="readout mono">
      <span className="readout-ghost" aria-hidden="true">88:88</span>
      <span className="readout-val" data-testid="decision-window-timer">{`00:${String(left).padStart(2, "0")}`}</span>
    </div>
    <div className="drain" aria-hidden="true">
      {SEGMENTS.map((i) => <span key={i} className={i < left ? "on" : ""} />)}
    </div>
  </div>
);

export const OperatorConsole = ({ onReject }) => {
  const ref = useRef(null);
  const [status, setStatus] = useState("idle");
  const [left, setLeft] = useState(TOTAL);
  const [logs, setLogs] = useState([]);
  const running = status === "running";

  useEffect(() => {
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setStatus((s) => (s === "idle" ? "running" : s));
          io.disconnect();
        }
      },
      { threshold: 0.4 }
    );
    io.observe(ref.current);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (!running) return;
    const id = setInterval(() => setLeft((l) => Math.max(l - 1, 0)), 1000);
    return () => clearInterval(id);
  }, [running]);

  useEffect(() => {
    if (running && left === 0) {
      setStatus("timeout");
      setLogs((l) => [...l, { t: stamp(TOTAL), text: WINDOW.timeout, tone: "crimson" }, { text: WINDOW.stillLocked, tone: "muted" }]);
    }
  }, [running, left]);

  const resolved = status === "ratified" || status === "timeout";
  const rejected = status === "rejected";

  const confirm = () => {
    if (resolved || rejected) return;
    const elapsed = TOTAL - left;
    setStatus("ratified");
    setLogs((l) => [...l, { t: stamp(elapsed), text: WINDOW.ratified(elapsed), tone: "crimson" }, { text: WINDOW.stillLocked, tone: "muted" }]);
  };

  const reject = () => {
    if (!rejected) {
      setStatus("rejected");
      setLogs((l) => [...l, { t: stamp(TOTAL - left), text: WINDOW.rejected, tone: "cyan" }]);
    }
    onReject();
  };

  return (
    <section id="finestra-30-secondi" className="sec console-sec bg-b" data-testid="decision-window-section" data-rail="W">
      <RegMarks />
      <div className="frame">
        <SectionMark n="05/06" label="INTERMEZZO OPERATIVO" />
        <Reveal className="console" data-state={status} style={{ "--drain": (TOTAL - left) / TOTAL }}>
          <div ref={ref} className="c-bar mono" aria-hidden="true">
            <span>TTY/03 — POSTAZIONE OPERATORE</span>
            <span className={`c-status ${running ? "is-live" : ""}`}>{status.toUpperCase()}</span>
          </div>
          <div className="c-main">
            <div className="c-log mono" data-testid="decision-window-log">
              <p className="c-title" data-testid="decision-window-title">{WINDOW.title}</p>
              {WINDOW.lines.map((l) => <p key={l}>{l}</p>)}
              {logs.map((l, i) => (
                <p key={i} className={`log-line tone-${l.tone}`} data-testid={`decision-audit-log-${i}`}>
                  {l.t && <span className="log-time">{`[${l.t}] `}</span>}
                  {l.text}
                </p>
              ))}
              <span className="cursor" aria-hidden="true" />
            </div>
            <Readout left={left} rejected={rejected} />
          </div>
          <div className="c-actions">
            <button
              type="button"
              onClick={confirm}
              disabled={resolved || rejected}
              className={`btn-confirm mono ${!resolved && !rejected ? "is-preselected" : ""}`}
              data-testid="decision-confirm-btn"
            >
              <span>{WINDOW.confirm}</span>
              {!resolved && !rejected && <span className="preselect-tag">{WINDOW.preselected}</span>}
            </button>
            <button type="button" onClick={reject} className="btn-reject mono" data-testid="decision-reject-btn">
              {WINDOW.reject}
            </button>
          </div>
          <div className="c-lock mono" data-testid="decision-lock-status">
            <span>{WINDOW.lockLabel}</span>
            <span className={rejected ? "lock-open" : "lock-closed"}>{rejected ? WINDOW.unlocked : WINDOW.locked}</span>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
